import { formatMoney, reserveWords, splitBudget, splitWords, type BudgetCeiling, type BudgetDto, type BudgetReserve, type ProposalDto } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { MoneyInput } from '../../components/MoneyInput.tsx';
import { RemindAsk, RemindLink } from '../../components/Remind.tsx';
import { useRemind } from '../../components/useRemind.ts';
import { toldLine } from '../../components/remindWords.ts';
import { Button, Choices, ErrorLine, Field, LoadError, Loading, Page, Title } from '../../components/ui.tsx';
import { useUnsaved } from '../../components/useUnsaved.ts';
import { proposalChanges, proposalStart } from '../formChanges.ts';
import { ProjectCrumb } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { dateWords } from '../../days.ts';

/**
 * The client's budget (feature spec "client page" §4; Jason, 2026-10-03):
 * what they can spend, all in, with the contingency held inside the amount or
 * on top of it (L-9, Jason, 2026-10-07). It changes only when both sides
 * agree: one proposes, with a note and where the contingency sits; the other
 * confirms it, as proposed or with the contingency moved, or declines it, with
 * a note of their own. Every split is core `splitBudget`. All of it is kept.
 */

const budgetKey = (projectId: string) => ['project', projectId, 'budget'];
const day = (iso: string) => dateWords(iso, 'date');
const ANSWERED = { confirmed: 'confirmed it', declined: 'declined it', withdrawn: 'withdrew it' } as const;

/** The two places for the contingency, each with the split it makes of this amount; null while there is no amount. */
function reserveOptions(amount: number | null, pct: number, label: (r: BudgetReserve) => string) {
  return (['inside', 'on_top'] as const).map((r) => ({
    value: r,
    label: label(r),
    hint: amount !== null && amount > 0 ? splitWords(splitBudget(amount, pct, r)) : r === 'inside' ? 'All in: the contingency is held back from it.' : 'For the work: the contingency is added to it.',
  }));
}

/** The split as a sentence's end: "$360,000 for the work and $40,000 held as contingency inside it". */
const splitSentence = (c: BudgetCeiling) =>
  `${formatMoney(c.workCents)} for the work and ${formatMoney(c.contingencyCents)} held as contingency ${c.reserve === 'on_top' ? 'on top' : 'inside it'}`;

export function Budget() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useApi<BudgetDto>(budgetKey(projectId), `/projects/${projectId}/budget`);
  useFeedbackRecord(projectId, project ? { type: 'project', id: projectId, label: `${project.name} — budget` } : null);
  if (error) return <LoadError error={error} />;
  if (!data || !project) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  const staff = project.viewerRole === 'contractor';
  const c = data.ceiling;
  return (
    <Page>
      <ProjectCrumb project={project} />
      <Title
        how={{
          line: staff ? 'What the client can spend, all in.' : 'The most you can spend, all in.',
          intent: 'The upper limit on the price bar, with a contingency held inside it.',
          steps: staff
            ? ['Propose the budget the client gave you; the client confirms it.', 'A change the client proposes waits here for you to confirm or decline.', 'Every change is kept below.']
            : ['Propose a budget, or confirm the one NSB proposed.', 'A change waits until the other side confirms it.', 'Every change is kept below.'],
        }}
      >
        Budget
      </Title>
      <p className="font-display text-[1.375rem] leading-snug sm:text-[1.5rem]">
        {c ? (
          // A sentence in the display face, numbers and all, as the cost bar's is.
          `${staff ? 'The client’s budget is' : 'Your budget is'} ${formatMoney(c.totalCents)}, all in${c.contingencyCents > 0 ? `: ${splitSentence(c)}.` : '.'}`
        ) : staff ? (
          'The client has not set a budget yet.'
        ) : (
          'There is no budget yet.'
        )}
      </p>

      {data.pending ? <Pending projectId={projectId} p={data.pending} staff={staff} first={!c} /> : <Propose projectId={projectId} data={data} staff={staff} />}

      {data.history.length ? (
        <section className="mt-12">
          <h2 className="border-b border-ink/40 pb-2 text-[1.375rem]">Every change</h2>
          <ol className="divide-y divide-rule">
            {data.history.map((p) => (
              <li key={p.id} className="py-3">
                <p>
                  <span className="num meta mr-3">{day(p.at)}</span>
                  {p.by} proposed <span className="num">{formatMoney(p.amountCents)}</span>
                  {p.contingencyPct > 0 ? ` with ${reserveWords(p.reserve)}` : null}
                  {p.byRole === 'client' ? <span className="label ml-2 align-[0.1em]">Client-driven</span> : null}
                </p>
                {p.note ? <p className="mt-1 whitespace-pre-line text-sm text-ink-2">“{p.note}”</p> : null}
                {p.answer ? (
                  <p className={`mt-1 text-sm ${p.answer.answer === 'confirmed' ? 'text-moss-deep' : 'text-ink-2'}`}>
                    <span className="num meta mr-3">{day(p.answer.at)}</span>
                    {p.answer.by} {ANSWERED[p.answer.answer]}
                    {p.answer.reserve && p.answer.reserve !== p.reserve ? ` with ${reserveWords(p.answer.reserve)}` : null}
                    {p.answer.note ? <span className="text-ink-2">: “{p.answer.note}”</span> : null}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-cedar">Waiting on {p.byRole === 'client' ? 'NSB' : 'the client'}</p>
                )}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </Page>
  );
}

function useSend(projectId: string) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async (path: string, body: unknown) => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(budgetKey(projectId), await api<BudgetDto>(path, { body }));
      // The cost bar, the homes and the timeline move with it.
      await queryClient.invalidateQueries({ queryKey: ['project', projectId], predicate: (q) => q.queryKey[2] !== 'budget' });
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, send };
}

/**
 * A change waiting: the other side confirms it, as proposed or with the contingency moved (inside to on top adds it to
 * the amount; on top to inside takes it from within), or declines it; its own side may withdraw it. It reads as it was
 * proposed, and the confirmation as the place chosen splits it.
 */
function Pending({ projectId, p, staff, first }: { projectId: string; p: ProposalDto; staff: boolean; first: boolean }) {
  const [note, setNote] = useState('');
  const [reserve, setReserve] = useState<BudgetReserve>(p.reserve);
  const { busy, err, send } = useSend(projectId);
  // NSB's own proposal waiting on the client: who was told of it, and Remind them (design study 26).
  const remind = useRemind(projectId, 'budget', p.id, { staff: staff && p.yours });
  const answer = (a: 'confirmed' | 'declined' | 'withdrawn') =>
    void send(`/projects/${projectId}/budget/${p.id}/answer`, { answer: a, note: note.trim() || null, ...(a === 'confirmed' ? { reserve } : {}) });
  const { contingencyPct } = p;
  const amount = formatMoney(p.amountCents);
  const chosen = splitBudget(p.amountCents, contingencyPct, reserve);
  const total = formatMoney(chosen.totalCents);
  const split = chosen.contingencyCents > 0 ? `: ${splitSentence(chosen)}` : '';
  return (
    <section className="mt-10 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
      <p className="label !text-cedar">{p.waitingOnYou ? 'Waiting on you' : `Waiting on ${staff ? 'the client' : 'NSB'}`}</p>
      <p className="mt-2 text-[1.0625rem]">
        {p.yours ? (staff ? 'NSB proposed' : 'You proposed') : p.by + ' proposed'} a budget of <span className="num">{amount}</span>
        {contingencyPct > 0 ? ` with ${reserveWords(p.reserve)}` : ', all in'}, on <span className="num">{day(p.at)}</span>.
        {staff && p.byRole === 'client' ? <span className="label ml-2 align-[0.1em]">Client-driven change</span> : null}
      </p>
      {contingencyPct > 0 ? <p className="meta mt-1">{splitWords(p.split)}</p> : null}
      {p.note ? <p className="mt-2 whitespace-pre-line text-ink-2">“{p.note}”</p> : null}
      {p.waitingOnYou ? (
        <>
          {contingencyPct > 0 ? (
            <div className="mt-5">
              <Choices
                legend={`The ${contingencyPct}% contingency`}
                value={reserve}
                onPick={setReserve}
                disabled={busy}
                options={reserveOptions(p.amountCents, contingencyPct, (r) => `${r === 'inside' ? `Inside ${amount}` : `On top of ${amount}`}${r === p.reserve ? ', as proposed' : ''}`)}
              />
            </div>
          ) : null}
          <p className="mt-4 text-ink-2">
            {staff
              ? `Confirm it, and the client’s budget is ${total}, all in${split}.`
              : first
                ? `Confirm it if ${total} is the most you can spend, all in${split}. If it means something else to you, decline it with a note.`
                : `Confirm it, and your budget is ${total}, all in${split}.`}
          </p>
          <Field label="A note" hint="Optional. It is kept with your answer.">
            {(id) => <textarea id={id} rows={2} className="field mt-1 resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Button busy={busy} onClick={() => answer('confirmed')}>
              Confirm it
            </Button>
            <Button tone="secondary" disabled={busy} onClick={() => answer('declined')}>
              Decline it
            </Button>
          </div>
        </>
      ) : p.yours ? (
        <div className="mt-4">
          <p className="text-ink-2">Until {staff ? 'the client confirms' : 'NSB confirms'} it, nothing changes.</p>
          {staff && remind.people.length ? <p className="meta mt-1">{toldLine(remind.people, remind.today)}</p> : null}
          {remind.open ? null : (
            <div className="mt-1 flex flex-wrap gap-x-6">
              <Button tone="quiet" busy={busy} onClick={() => answer('withdrawn')}>
                Withdraw it
              </Button>
              <RemindLink r={remind} className="!text-base" />
            </div>
          )}
          <RemindAsk r={remind} />
        </div>
      ) : null}
      <ErrorLine error={err} />
    </section>
  );
}

/**
 * Propose a budget: the client's NSB confirms; NSB's, the client confirms.
 * The first is the number alone, with where the contingency sits (inside it
 * unless chosen otherwise); a change after it starts from the budget as
 * confirmed and says why (Jason, 2026-10-04: "the first budget proposal
 * should just have the number input, always note when the contractor will
 * confirm a change"). No words are put in anyone's mouth: the note starts
 * empty.
 */
function Propose({ projectId, data, staff }: { projectId: string; data: BudgetDto; staff: boolean }) {
  const c = data.ceiling;
  const [open, setOpen] = useState(false);
  const [reserve, setReserve] = useState<BudgetReserve>(c?.reserve ?? 'inside');
  const [amount, setAmount] = useState<number | null>(c ? (c.reserve === 'on_top' ? c.workCents : c.totalCents) : null);
  // What the field reads as it is typed: the split beside it follows it before it is committed.
  const [typed, setTyped] = useState<number | null>(amount);
  const [note, setNote] = useState('');
  const { busy, err, send } = useSend(projectId);
  const propose = () => void send(`/projects/${projectId}/budget`, { amountCents: amount, reserve, note: note.trim() || null }).then((ok) => ok && setOpen(false));
  // Opened and changed, not proposed yet: the bar at the window's foot says so, its button Propose it (P-29).
  useUnsaved({
    names: open ? proposalChanges(c, { amount: typed, reserve, note }, { pct: data.contingencyPct, staff }) : [],
    lead: 'Not proposed yet',
    saveWords: 'Propose it',
    busy,
    // As typed: pressing it leaves the field, which commits the amount before it is proposed.
    canSave: typed !== null && typed > 0,
    error: err,
    onSave: propose,
    onDiscard: () => {
      const s = proposalStart(c);
      setAmount(s.amount);
      setTyped(s.amount);
      setReserve(s.reserve);
      setNote(s.note);
      setOpen(false);
    },
  });
  if (!open) {
    return (
      <div className="mt-8">
        <Button onClick={() => setOpen(true)}>{c ? 'Adjust the budget' : staff ? 'Propose the budget' : 'Set a budget'}</Button>
      </div>
    );
  }
  const pct = data.contingencyPct;
  return (
    <section className="mt-8 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
      <p className="label">{c ? (staff ? 'A change NSB proposes' : 'A change to your budget') : staff ? 'The client’s budget' : 'Your budget'}</p>
      <Field label="The budget" hint={pct > 0 ? undefined : 'No contingency is held on this project.'}>
        {(id) => (
          <MoneyInput
            id={id}
            value={amount}
            onChange={(v) => {
              setAmount(v);
              setTyped(v);
            }}
            onTyping={setTyped}
          />
        )}
      </Field>
      {pct > 0 ? (
        <Choices
          legend={`The ${pct}% contingency`}
          value={reserve}
          onPick={setReserve}
          disabled={busy}
          options={reserveOptions(typed, pct, (r) => (r === 'inside' ? 'Inside this amount' : 'On top of this amount'))}
        />
      ) : null}
      {c ? (
        <Field label={staff ? 'Why' : 'What changed'} hint="Kept with the change; the other side reads it.">
          {(id) => <textarea id={id} rows={2} className="field resize-y" value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      ) : null}
      <p className="mb-4 text-sm text-ink-2">
        {staff ? 'The client will confirm it' : 'NSB will confirm it'}
        {c ? `; until then ${staff ? 'the' : 'your'} budget stays ${formatMoney(c.totalCents)}, all in.` : '.'}
      </p>
      <div className="flex gap-5">
        <Button busy={busy} disabled={amount === null || amount <= 0} onClick={propose}>
          Propose it
        </Button>
        <Button tone="quiet" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </section>
  );
}
